import type {BarcodeScannerService} from './barcode-scanner.service';
import type {IAttributes, IAugmentedJQuery, IDirective, INgModelController, IOnDestroy, IScope} from 'angular';
import {distinctUntilChanged, take, tap} from 'rxjs/operators';
import type {Subscription} from 'rxjs';
import {Barcode} from './barcode';
import bind from 'bind-decorator';
import type {UserFeedbackService} from '../commons/services/user-feedback.service';

class KpBarcodeScannerDirectiveController implements IOnDestroy {

    private subscription: Subscription;
    private ngModelController: INgModelController; // input model reference
    private element: HTMLInputElement; // native self reference

    private readonly focusEvent = 'focus';
    private readonly blurEvent = 'blur';
    private readonly INVALID_ELEMENT_MSG = 'Barcode scanner directive requires input element';

    /*@ngInject*/
    constructor(
        private $element: IAugmentedJQuery,
        private barcodeScannerService: BarcodeScannerService,
        private userFeedbackService: UserFeedbackService,
        private $scope: IScope,
        private $attrs: IAttributes) {

        if (!($element[0] instanceof HTMLInputElement)) {
            throw new TypeError(this.INVALID_ELEMENT_MSG);
        }
        this.element = $element[0];
    }

    // get model reference from directive link hook
    public init(ngModelController: INgModelController) {

        this.ngModelController = ngModelController;
        if (this.barcodeScannerService.isUsable()) {

            this.element.addEventListener(this.focusEvent, this.focusEventHandler);
            this.element.addEventListener(this.blurEvent, this.blurEventHandler);

            this.barcodeScannerService.registerBarcodeScannerConsumer();
        }
    }

    public $onDestroy(): void {

        this.unsubscribeValues();

        if (this.barcodeScannerService.isUsable()) {

            this.barcodeScannerService.unregisterBarcodeScannerConsumer();

            this.element.removeEventListener(this.focusEvent, this.focusEventHandler);
            this.element.removeEventListener(this.blurEvent, this.blurEventHandler);
        }
    }

    // eslint-disable-next-line no-invalid-this
    private focusEventHandler = () => this.subscribeValues();

    // eslint-disable-next-line no-invalid-this
    private blurEventHandler = () => this.unsubscribeValues();

    private subscribeValues() {

        this.subscription = this.barcodeScannerService.getScannedBarcode$()
            .pipe(
                distinctUntilChanged(Barcode.equals),
                take(1), // take only 1 barcode, then unsubscribe
                tap(this.signalSuccessfulScan), // beep & vibrate
                tap(() => this.barcodeScannerService.stopScanning())) // stop scanning
            .subscribe(this.fillInputValue);
    }

    private unsubscribeValues() {

        if (this.subscription) {
            this.subscription.unsubscribe()
            this.subscription = null;
        }
    }

    @bind
    private fillInputValue(barcode: Barcode) {
        this.ngModelController.$setViewValue(barcode.text);
        this.ngModelController.$render();

        if (this.$attrs[KpBarcodeScannerDirective.directiveName]) {
            this.$scope.$eval(this.$attrs[KpBarcodeScannerDirective.directiveName]);
        }
    }

    @bind
    private signalSuccessfulScan() {
        this.userFeedbackService.beep(300);
        this.userFeedbackService.vibrate(500);
    }
}


/**
 * @ngdoc directive
 * @name kpBarcodeScanner
 * @module portaro.barcode-scanner
 *
 * @restrict A
 *
 * @requires ngModel
 *
 * @param {expression} kpBarcodeScanner On scan hook
 *
 * @description
 * This directive uses {@Link BarcodeScannerService} to fill text input by values from scanned codes
 */
export class KpBarcodeScannerDirective implements IDirective {

    public static readonly directiveName = 'kpBarcodeScanner';

    public restrict = 'A';
    public controller = KpBarcodeScannerDirectiveController;
    public require = [KpBarcodeScannerDirective.directiveName, 'ngModel'];

    public link($scope: IScope,
                $element: IAugmentedJQuery,
                $attrs: IAttributes,
                [controller, ngModel]: [KpBarcodeScannerDirectiveController, INgModelController]) {

        controller.init(ngModel);
    }
}